import { boolean, InferType, number, object, string, array } from 'yup'
import { AnyObject, ValidateOptions } from 'yup'
import { accessorBuilder } from '.'
import { phoneNumberExpression } from './expressions'
import { isNumber } from './guards'

// TODO: Yup context is missing index
type ValidateOptionsExtended<T> = ValidateOptions<T> & {
  index: number
}

export const minQuantityArray = accessorBuilder<number[]>([])
export const maxQuantity = accessorBuilder(9999)

export const settingsSchema = object({
  tax_rate: number()
    .positive()
    .nullable()
    .min(1)
    .max(100)
    .transform((v) => (isNaN(v) ? null : v))
    .label('tax rate')
    .typeError('must be a number'),
  strict_card_authorisation: boolean(),
  pass_processing_fee_to_consumer: boolean(),
})

export type SettingsSchema = InferType<typeof settingsSchema>

export const detailsSchema = object({
  details: array().of(
    object({}).shape(
      {
        id: number().required().label('serie id'),
        quantity: number()
          .integer()
          .max(9999)
          .nullable()
          .transform((v) => (isNaN(v) ? null : v))
          .test('dynamic-min', (value, context) => {
            const idx = (context.options as ValidateOptionsExtended<AnyObject>)
              .index

            if (
              isNumber(value) &&
              (value === 0 || value < minQuantityArray.currentValue[idx])
            ) {
              return context.createError({
                message: `quantity must be at least ${minQuantityArray.currentValue[idx]}`,
              })
            }

            return true
          })
          .when('price', {
            is: (value: number | null) => value && String(value).length > 0,
            then: (schema: any) => schema.required(),
          })
          .label('quantity')
          .typeError('must be a number'),
        price: number()
          .min(1)
          .max(9999)
          .nullable()
          .transform((v) => (isNaN(v) ? null : v))
          .when('quantity', {
            is: (value: number | null) => value && String(value).length > 0,
            then: (schema: any) => schema.required(),
          })
          .label('price')
          .typeError('must be a number'),
        sales_from: string().required().label('sales from'),
        sales_to: string().required().label('sales to'),
        name: string().label('event title'),
      },
      [['quantity', 'price']],
    ),
  ),
})

export type DetailsSchema = InferType<typeof detailsSchema>

export const checkoutSchema = object({
  serie_id: number().required(),
  quantity: number()
    .positive()
    .integer()
    .nullable()
    .min(1)
    .required()
    .transform((v) => (isNaN(v) ? null : v))
    .test(
      'dynamic-max',
      ({ path }) =>
        `${path} must be less than or equal to ${maxQuantity.currentValue}`,
      (value, context) => {
        if (value && value > maxQuantity.currentValue) {
          return context.createError({
            message: `${context.path} must be less than or equal to ${maxQuantity.currentValue}`,
          })
        }

        return true
      },
    )
    .label('quantity'),
})

export type CheckoutSchema = InferType<typeof checkoutSchema>

export const paymentSchema = object({
  first_name: string().required().label('first name'),
  last_name: string().required().label('last name'),
  phone: string()
    .matches(phoneNumberExpression, 'phone number is invalid')
    .required()
    .label('phone number'),
  email: string().email().required().label('e-mail'),
  card: string().required(),
})

export type PaymentSchema = InferType<typeof paymentSchema>

export const paymentBillingSchema = object({
  billing_name: string().required().label('name on card'),
  billing_phone: string()
    .matches(phoneNumberExpression, 'phone number is invalid')
    .required()
    .label('billing phone number'),
  billing_email: string().email().required().label('billing e-mail'),
  billing_address: string().required().label('billing address'),
})

export type PaymentBillingSchema = InferType<typeof paymentBillingSchema>
